import { useEffect, useState, type JSX } from 'react';
import {
  FOURNISSEURS_CONNEXION,
  LIBELLES_USAGE_CONNEXION,
  fournisseurConnexion,
  type FournisseurConnexion,
} from '@taki/data-model';
import {
  Button,
  ConfirmDialog,
  LogoMarque,
  SettingsActions,
  SettingsAddMenu,
  SettingsEmpty,
  SettingsGroup,
  SettingsInfo,
  SettingsInput,
  SettingsMenu,
  SettingsMessage,
  SettingsRow,
  SettingsSection,
  SettingsTable,
  SkeletonText,
  StatusBadge,
  Switch,
  estMarqueConnecteur,
  type ConnecteurReglagesView,
  type ConnecteurView,
  type ConnecteursPort,
} from '@taki/ui';

import { adresseSansRetour, derniereUtilisation, messageDuRetour, type MessageDeRetour } from './retourConnecteur.js';

/**
 * Les fournisseurs du registre qui ouvrent un agenda ou une messagerie a Lia,
 * par OAuth (le menu « Ajouter »), dans son ordre. Les connecteurs de Flux
 * (Slack, Google Sheets...) se relient dans les Connexions de Flux, pas ici.
 */
const pourLia = (f: FournisseurConnexion): boolean => f.authentification === 'oauth2' && f.services.some((s) => s === 'mail' || s === 'agenda');
const FOURNISSEURS_OAUTH = FOURNISSEURS_CONNEXION.filter((f) => f.authentification === 'oauth2').filter(pourLia);
function fournisseurOAuth(id: string): FournisseurConnexion | null {
  const f = fournisseurConnexion(id);
  return f !== undefined && pourLia(f) ? f : null;
}
const LIBELLES_STATUT: Record<ConnecteurView['statut'], string> = { active: 'Connecté', a_renouveler: 'À reconnecter', erreur: 'En erreur' };

/** L'id de la section, pour y ramener la vue au retour du fournisseur. */
const ANCRE = 'connecteurs';

function message(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * LE RETOUR DU CONSENTEMENT, LU AU CHARGEMENT DE LA PAGE ET UNE SEULE FOIS. Le
 * retour du fournisseur est un chargement complet : on lit l'adresse ici, avant
 * que le routeur ne la reecrive (sa validation ne garde que `t`), et on la
 * consomme au premier montage, pour qu'une visite suivante de l'onglet ne
 * redise rien.
 */
let retourDuChargement: MessageDeRetour | null = typeof window === 'undefined' ? null : messageDuRetour(window.location.search);

/* PURE (le mode strict l'appelle deux fois) : la consommation se fait dans l'effet de montage. */
function lireRetour(): MessageDeRetour | null {
  return retourDuChargement ?? messageDuRetour(window.location.search);
}

/** Le nom a l'ecran : le nom affiche, sinon celui du fournisseur. */
function nomDe(connexion: ConnecteurView, fournisseur: FournisseurConnexion | null): string {
  return connexion.nom.trim() !== '' ? connexion.nom : (fournisseur?.libelle ?? connexion.fournisseur);
}

/**
 * LA SECTION « CONNECTEURS » DE PARAMETRES > CONVERSATIONS : les comptes Google
 * et Microsoft (agenda, mail) que le membre ouvre a Lia. Le tableau suit celui
 * des Utilisateurs (`MembresSettingsPanel`) : une ligne par connexion, son nom
 * deplie ses reglages sur place. « Ajouter » part chez le fournisseur en un
 * clic (le consentement EST l'ajout), et le retour se dit en une ligne.
 *
 * PERSONNELLE : la section ne montre que les connexions du membre, et personne
 * d'autre, administrateur compris, ne les voit (les rules, et l'hote qui filtre
 * `auteurUid`). Le contrat : `ConnecteursPort` (`@taki/ui`), le registre des
 * fournisseurs : celui des connexions du socle (`@taki/data-model`, `connexions.ts`).
 */
export function ConnecteursSection({ port }: { port: ConnecteursPort | undefined }): JSX.Element {
  const [retour, setRetour] = useState<MessageDeRetour | null>(lireRetour);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [aRetirer, setARetirer] = useState<ConnecteurView | null>(null);
  const [depart, setDepart] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  /* L'ADRESSE PERD LES PARAMETRES DU RETOUR, et la vue vient a la section : le
     message est ecrit la, pas en haut de l'onglet. `history.state` est garde,
     le routeur y range sa clef. */
  useEffect(() => {
    retourDuChargement = null;
    const propre = adresseSansRetour(window.location.href);
    if (propre !== null) window.history.replaceState(window.history.state, '', propre);
    if (retour !== null) document.getElementById(ANCRE)?.scrollIntoView({ block: 'start' });
  }, []);

  if (port === undefined) {
    return <SettingsSection id={ANCRE} title="Connecteurs" contextLabel="Votre compte">
      <SettingsInfo>Indisponible dans cet environnement.</SettingsInfo>
    </SettingsSection>;
  }

  const connecter = (fournisseur: string): void => {
    setDepart(fournisseur);
    setErreur(null);
    setRetour(null);
    /* EN CAS DE SUCCES, LE NAVIGATEUR QUITTE LA PAGE : le bouton reste en attente. */
    port.connecter(fournisseur).catch((cause: unknown) => {
      setErreur(message(cause));
      setDepart(null);
    });
  };

  const retirer = (connexion: ConnecteurView): void => {
    setARetirer(null);
    setErreur(null);
    setRetour(null);
    port.retirer(connexion.id).then(
      ({ revoque }) => {
        if (ouverte === connexion.id) setOuverte(null);
        setRetour({ tone: 'status', texte: revoque ? 'Connecteur retiré.' : 'Connecteur retiré. Accès à révoquer chez le fournisseur.' });
      },
      (cause: unknown) => setErreur(message(cause)),
    );
  };

  const ajouts = FOURNISSEURS_OAUTH.map((f) => ({
    value: f.id,
    label: f.libelle,
    icone: estMarqueConnecteur(f.id) ? <LogoMarque marque={f.id} taille={20} /> : undefined,
    /* `null` : on ne sait pas encore, le serveur tranchera. */
    ...(port.configures !== null && !port.configures.has(f.id) ? { inactif: 'Non configuré' } : {}),
  }));

  return <SettingsSection id={ANCRE} title="Connecteurs" contextLabel="Votre compte">
    {retour === null ? null : <SettingsMessage tone={retour.tone}>{retour.texte}</SettingsMessage>}
    {erreur === null ? null : <SettingsMessage tone="error">{erreur}</SettingsMessage>}
    {!port.ready ? <SkeletonText lines={2} /> : (
      <SettingsTable
        label="Connecteurs"
        columns={[
          { label: 'Connecteur' },
          { label: 'Compte' },
          { label: 'Accès' },
          { label: 'État' },
          { label: 'Dernière utilisation' },
        ]}
        empty="Aucun connecteur."
        rows={port.connexions.filter((connexion) => fournisseurOAuth(connexion.fournisseur) !== null).map((connexion) => {
          const fournisseur = fournisseurOAuth(connexion.fournisseur);
          const niveau = fournisseur?.niveaux.find((n) => n.id === connexion.acces);
          const deplie = ouverte === connexion.id;
          return {
            id: connexion.id,
            attenue: connexion.statut !== 'active',
            cells: [
              <Button key="nom" variant="ghost" size="sm" aria-expanded={deplie}
                onClick={() => setOuverte(deplie ? null : connexion.id)}>
                {estMarqueConnecteur(connexion.fournisseur) ? <LogoMarque marque={connexion.fournisseur} taille={20} /> : null}
                {nomDe(connexion, fournisseur)}
              </Button>,
              <span key="compte" className="break-all text-ink-700">{connexion.compte}</span>,
              niveau?.libelle ?? connexion.acces,
              <StatusBadge key="etat" label={LIBELLES_STATUT[connexion.statut]} tone={connexion.statut === 'active' ? 'success' : 'danger'} />,
              derniereUtilisation(connexion.derniereUtilisation),
            ],
            ...(deplie && fournisseur !== null ? {
              detail: <DetailConnecteur key={connexion.id} port={port} connexion={connexion} fournisseur={fournisseur}
                onRetirer={() => setARetirer(connexion)} />,
            } : {}),
          };
        })}
      />
    )}
    <SettingsActions>
      <SettingsAddMenu options={ajouts} busy={depart !== null} onChoose={connecter} />
    </SettingsActions>
    <ConfirmDialog
      open={aRetirer !== null}
      tone="danger"
      title="Retirer ce connecteur ?"
      body={aRetirer === null ? '' : [`${nomDe(aRetirer, fournisseurOAuth(aRetirer.fournisseur))} (${aRetirer.compte})`, 'Lia n’y aura plus accès.']}
      confirmLabel="Retirer"
      onConfirm={() => { if (aRetirer !== null) retirer(aRetirer); }}
      onCancel={() => setARetirer(null)}
    />
  </SettingsSection>;
}

/**
 * LES REGLAGES D'UNE CONNEXION, deplies sous sa ligne. Chaque reglage
 * s'enregistre seul, au geste (le nom a la sortie du champ) : pas de bouton
 * Enregistrer pour quatre valeurs independantes. Un niveau d'acces qui demande
 * des droits en plus part chez le fournisseur (`port.regler`).
 *
 * LA VALEUR CHOISIE S'AFFICHE TOUT DE SUITE, puis cede a celle de la base des
 * qu'elle revient : sans ca, le menu montrerait l'ancien niveau le temps de
 * l'aller-retour.
 */
function DetailConnecteur({ port, connexion, fournisseur, onRetirer }: {
  port: ConnecteursPort;
  connexion: ConnecteurView;
  fournisseur: FournisseurConnexion;
  onRetirer: () => void;
}): JSX.Element {
  const [nom, setNom] = useState(connexion.nom);
  const [enAttente, setEnAttente] = useState<ConnecteurReglagesView>({});
  const [choix, setChoix] = useState<{ id: string; nom: string; principal: boolean }[] | null>(null);
  const [erreurChoix, setErreurChoix] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [reconnexion, setReconnexion] = useState(false);

  useEffect(() => { setNom(connexion.nom); }, [connexion.nom]);

  /* LES AGENDAS OU DOSSIERS SE LISENT CHEZ LE FOURNISSEUR, a l'ouverture. */
  useEffect(() => {
    let vivant = true;
    port.choix(connexion.id).then(
      (options) => { if (vivant) setChoix(options); },
      (cause: unknown) => { if (vivant) { setChoix([]); setErreurChoix(message(cause)); } },
    );
    return () => { vivant = false; };
  }, [port, connexion.id]);

  const regler = (reglages: ConnecteurReglagesView): void => {
    setErreur(null);
    setEnAttente((avant) => ({ ...avant, ...reglages }));
    const oublier = (): void => setEnAttente((avant) => {
      const reste = { ...avant };
      for (const clef of Object.keys(reglages) as (keyof ConnecteurReglagesView)[]) delete reste[clef];
      return reste;
    });
    port.regler(connexion.id, reglages).then(oublier, (cause: unknown) => { oublier(); setErreur(message(cause)); });
  };

  const enregistrerNom = (): void => {
    const propre = nom.trim();
    if (propre === '' || propre === connexion.nom) { setNom(connexion.nom); return; }
    regler({ nom: propre });
  };

  const acces = enAttente.acces ?? connexion.acces;
  const usage = enAttente.usage ?? connexion.usage;
  const selection = enAttente.selection ?? connexion.selection.map((s) => s.id);
  const niveau = fournisseur.niveaux.find((n) => n.id === acces);
  const libelleSelection = fournisseur.oauth?.selection ?? 'Sélection';
  const singulier = libelleSelection.toLocaleLowerCase('fr').replace(/s$/, '');
  const parDefaut = fournisseur.services.includes('agenda') ? 'Aucun coché : l’agenda principal.' : `Aucun coché : tous les ${libelleSelection.toLocaleLowerCase('fr')}.`;

  const basculer = (id: string, inclus: boolean): void => {
    regler({ selection: inclus ? [...selection.filter((s) => s !== id), id] : selection.filter((s) => s !== id) });
  };

  return <>
    <SettingsGroup title="Réglages">
      <SettingsRow label="Nom affiché">
        <SettingsInput aria-label="Nom affiché" value={nom} maxLength={80} placeholder={fournisseur.libelle}
          onChange={(event) => setNom(event.target.value)} onBlur={enregistrerNom}
          onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
      </SettingsRow>
      <SettingsRow label="Accès" hint={niveau?.detail}>
        <SettingsMenu label="Accès" value={acces}
          options={fournisseur.niveaux.map((n) => ({ value: n.id, label: n.libelle, detail: n.detail }))}
          onChange={(valeur) => regler({ acces: valeur })} />
      </SettingsRow>
      <SettingsRow label="Utilisable par">
        <SettingsMenu label="Utilisable par" value={usage}
          options={Object.entries(LIBELLES_USAGE_CONNEXION).map(([value, label]) => ({ value, label }))}
          onChange={(valeur) => regler({ usage: valeur === 'lia_flux' ? 'lia_flux' : 'lia' })} />
      </SettingsRow>
    </SettingsGroup>
    <SettingsGroup title={libelleSelection}>
      {choix === null ? <SkeletonText lines={2} />
        : erreurChoix !== null ? <SettingsMessage tone="error">{erreurChoix}</SettingsMessage>
        : choix.length === 0 ? <SettingsEmpty>{`Aucun ${singulier}.`}</SettingsEmpty>
        : choix.map((option) => (
          <SettingsRow key={option.id} label={option.nom} badge={option.principal ? 'Principal' : undefined}>
            <Switch checked={selection.includes(option.id)} aria-label={`Inclure ${option.nom}`}
              onChange={(inclus) => basculer(option.id, inclus)} />
          </SettingsRow>
        ))}
      {choix !== null && choix.length > 0 ? <SettingsInfo>{parDefaut}</SettingsInfo> : null}
    </SettingsGroup>
    {erreur === null ? null : <SettingsMessage tone="error">{erreur}</SettingsMessage>}
    <SettingsActions>
      <Button variant="secondary" size="sm" loading={reconnexion}
        onClick={() => {
          setReconnexion(true);
          setErreur(null);
          port.connecter(connexion.fournisseur, { connexionId: connexion.id }).catch((cause: unknown) => {
            setReconnexion(false);
            setErreur(message(cause));
          });
        }}>Reconnecter</Button>
      <Button variant="secondary" size="sm" onClick={onRetirer}>Retirer</Button>
    </SettingsActions>
  </>;
}
